import { useMemo } from 'react'
import { Flex } from '@radix-ui/themes'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { IconX } from '@tabler/icons-react'
import { useGetQuestionDetail } from '@/services/questionDetailsService'
import {
  QuestionDetailsRes,
  CaseStudyQuestionType,
  QuestionDetailType,
} from '@/types/question.type'
import { useModulesStore } from '@/stores/useModulesStore'
import BreadScrum from '@/components/BreadScrum'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import {
  ParagraphQuestionDetail,
  CaseStudyQuestionDetail,
} from './components/QuestionDetail'
import { TypeValue } from './constant'
import { getQuestionType } from './utils'

const QuestionDetail = () => {
  const navigate = useNavigate()
  const { questionId, questionLibId } = useParams({
    from: '/_authenticated/question-lib/$questionLibId/$questionId',
  })
  const searchParams = useSearch({
    from: '/_authenticated/question-lib/$questionLibId/$questionId',
  }) as { source?: string }
  const isFromChapterTable = searchParams?.source === 'chapterTable'

  const { selectedModule, selectedChapter, selectedQuestion } =
    useModulesStore()

  const questionType = useMemo(() => {
    if (!selectedQuestion) {
      return ''
    }
    return selectedQuestion?.case_study_id
      ? TypeValue.case_study
      : selectedQuestion?.sub_questions[0]?.type
  }, [selectedQuestion])

  const { questionDetail, isLoadingQuestionDetail, refetchQuestionDetail } =
    useGetQuestionDetail({
      questionId,
      questionType,
    })

  // Type guard to check if questionDetail is QuestionDetailType
  const isQuestionDetailType = (
    data: QuestionDetailsRes | undefined
  ): data is QuestionDetailType => {
    return data !== undefined && 'text' in data && 'moodle_question_id' in data
  }

  // Type guard to check if questionDetail is CaseStudyQuestionType
  const isCaseStudyQuestionType = (
    data: QuestionDetailsRes | undefined
  ): data is CaseStudyQuestionType => {
    return data !== undefined && 'case_study_id' in data && 'case_study' in data
  }

  return (
    <Main className='h-full !text-[#002652]'>
      <BreadScrum
        breadcrumbs={[
          { link: '/question-lib/', label: 'Question Library' },
          { link: '', label: selectedModule?.name ?? '' },
          { link: '', label: selectedChapter?.chapter_name ?? '' },
          {
            link: '',
            label:
              selectedQuestion?.moodle_question_id ??
              selectedQuestion?.question_id.toString() ??
              '',
          },
        ]}
      />
      <Flex gap={'3'} align={'center'} justify={'between'}>
        <PageTitle title='Question Detail' />
        <button
          onClick={() =>
            navigate({
              to: `/question-lib/$quesiontionLibId`,
              params: { quesiontionLibId: questionLibId },
            })
          }
          className='mb-4 h-fit rounded p-3 hover:bg-[#bfd2e6]'
        >
          <IconX className='w-5' />
        </button>
      </Flex>

      {/* Render appropriate component based on question type */}
      {isQuestionDetailType(questionDetail) ? (
        <ParagraphQuestionDetail
          fromClass={isFromChapterTable}
          refetchQuestionDetail={() => refetchQuestionDetail()}
          questionDetail={questionDetail}
          isLoading={isLoadingQuestionDetail}
          questionType={getQuestionType(questionType)}
        />
      ) : isCaseStudyQuestionType(questionDetail) ? (
        <CaseStudyQuestionDetail
          fromClass={isFromChapterTable}
          refetchQuestionDetail={() => refetchQuestionDetail()}
          questionDetail={questionDetail}
          isLoading={isLoadingQuestionDetail}
          questionType={getQuestionType(questionType)}
        />
      ) : null}
    </Main>
  )
}

export default QuestionDetail
